/* Fixed header.
   =====================================================================
   The "Fixed header" theme setting renders `data-fixed-header` on the
   header; header.css makes it sticky and, once the page has scrolled, gives
   it the blurred background through the `fixed-header` class on <body> (the
   1.5.x contract). This module decides when that class applies: a 1px
   sentinel sits at the top of the page and an IntersectionObserver reports
   when it leaves the viewport, which costs nothing per frame. Engines
   without IntersectionObserver get a passive scroll listener instead. */

export default function initStickyHeader({ root = document } = {}) {
  const header = root.querySelector('[data-fixed-header]');
  if (!header || !header.parentNode) return null;

  const sentinel = document.createElement('div');
  sentinel.setAttribute('data-sticky-sentinel', '');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
  header.parentNode.insertBefore(sentinel, header);

  const set = (stuck) => document.body.classList.toggle('fixed-header', stuck);

  if (typeof IntersectionObserver === 'function') {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) set(!entry.isIntersecting);
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      sentinel.remove();
      set(false);
    };
  }

  const onScroll = () => set((window.scrollY || window.pageYOffset || 0) > 0);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  return () => {
    window.removeEventListener('scroll', onScroll);
    sentinel.remove();
    set(false);
  };
}
